import type { Unsubscribe } from '@exodus/atoms';
import type { Definition } from '@exodus/dependency-types';

import type { FetchWithDefaultHeaders } from '@/background/adapters/fetch';
import type { AnalyticsUserIdAtom } from '../atoms/user-id.js';
import type { ShareActivityAtom } from '../types.js';

const TELEMETRY_ID_HEADER = 'x-exodus-telemetry-id';

type Dependencies = {
  analyticsUserIdAtom: AnalyticsUserIdAtom;
  shareActivityAtom: ShareActivityAtom;
  fetch: FetchWithDefaultHeaders;
};

const createFetchHeaderPlugin = ({
  analyticsUserIdAtom,
  shareActivityAtom,
  fetch,
}: Dependencies) => {
  let unobserveUserId: Unsubscribe | undefined;
  let unobserveShareActivity: Unsubscribe | undefined;

  const onUnlock = () => {
    unobserveUserId = analyticsUserIdAtom.observe(async (userId) => {
      const shareActivity = await shareActivityAtom.get();

      fetch.setDefaultHeader(
        TELEMETRY_ID_HEADER,
        shareActivity ? userId : undefined,
      );
    });

    unobserveShareActivity = shareActivityAtom.observe(
      async (shareActivity) => {
        const userId = await analyticsUserIdAtom.get();

        fetch.setDefaultHeader(
          TELEMETRY_ID_HEADER,
          shareActivity ? userId : undefined,
        );
      },
    );
  };

  const onStop = () => {
    unobserveUserId?.();
    unobserveShareActivity?.();
  };

  return {
    onUnlock,
    onStop,
  };
};

const fetchHeaderPluginDefinition = {
  id: 'fetchHeaderPlugin',
  type: 'plugin',
  factory: createFetchHeaderPlugin,
  dependencies: ['analyticsUserIdAtom', 'shareActivityAtom', 'fetch'],
} as const satisfies Definition;

export default fetchHeaderPluginDefinition;
